// src/hooks/useBpeConversation.ts
import { useRef, useState, useCallback } from "react";
import { fetchWithAuth } from "../../../../api-client/api_client";
import { APIEndpointMethod } from "../../../../models/Entities";
import { showErrorToast } from "../../../shared/Toasts";
import {
  renderCustomComponent,
  toggleInputDisabled,
  toggleMsgLoader,
} from "@merge-api/merge-javascript-shared/dist/designSystem/molecules/ChatWidget/api";
import { MarkdownMessage } from "../components/MarkdownMessage";
import { Blueprint } from "../../../../models/Blueprints";
import useBlueprintContext from "../../../../components/blueprint-editor/context/useBlueprintContext";

interface BpeResponse {
  response_id: string;
  session_id?: string;
  content: string;
}

export function useBpeConversation(
  doesBlueprintHaveUnsavedChanges: boolean,
  blueprint: Blueprint,
  isUsingCustomerLinkedAccount: boolean
) {
  // ref for previous ID
  const prevIdRef = useRef<string | null>(null);
  // ref for previous blueprint version ID
  const prevVersionIdRef = useRef<string>(blueprint.version.id);
  // state for session ID
  const [sessionId, setSessionId] = useState<string | null>(null);
  // state for loading
  const [loading, setLoading] = useState(false);

  // Get step log context from blueprint context
  const { blueprintRunnerExecutionResponse, blueprintReadme } = useBlueprintContext();

  // helper to toggle both input & loader
  const toggleChatControls = () => {
    toggleInputDisabled();
    toggleMsgLoader();
  };

  const send = useCallback(
    (text: string) => {
      setLoading(true);
      toggleChatControls();

      // detect if version changed
      const versionChanged = prevVersionIdRef.current !== blueprint.version.id;

      // Only include step logs if not using a customer linked account
      const stepIOLogs = !isUsingCustomerLinkedAccount
        ? (blueprintRunnerExecutionResponse?.exit_data?.debugging_info?.step_io_logs ?? []).slice(
            -50
          )
        : [];

      fetchWithAuth({
        path: "/ai/blueprint-editor/conversation/",
        method: APIEndpointMethod.POST,
        headers: { "Content-Type": "application/json" },
        body: {
          has_changes: doesBlueprintHaveUnsavedChanges || versionChanged,
          prev_response_id: prevIdRef.current,
          blueprint_json: blueprint,
          prompt: text,
          session_id: sessionId,
          execution_context: {
            step_io_logs: stepIOLogs,
            exit_code: blueprintRunnerExecutionResponse?.exit_code,
            exception_message: blueprintRunnerExecutionResponse?.exception_message,
          },
          readme: blueprintReadme,
        },
        onResponse: (data: BpeResponse) => {
          setLoading(false);
          toggleChatControls();
          prevIdRef.current = data.response_id;
          setSessionId(data.session_id ?? null);
          renderCustomComponent(MarkdownMessage, {
            text: data.content,
            response_id: data.response_id,
            id: data.response_id,
          });
        },
        onError: (error) => {
          setLoading(false);
          toggleChatControls();
          error
            ?.json()
            .then((value: string | Record<string, any>) => {
              let errorMessage = "";
              if (typeof value == "string") {
                errorMessage = value;
              } else {
                errorMessage = value?.non_field_errors ?? JSON.stringify(value);
              }
              showErrorToast(`Failed to send message. Error message: \n ${errorMessage}`);
            })
            .catch(() => {
              showErrorToast("Failed to send message");
            });
        },
      });
    },
    [
      blueprint,
      doesBlueprintHaveUnsavedChanges,
      blueprintRunnerExecutionResponse,
      isUsingCustomerLinkedAccount,
    ]
  );

  return { send, loading };
}
